import _EditIcon from '@mui/icons-material/Edit';
import _DeleteIcon from '@mui/icons-material/Delete';
import _CachedIcon from '@mui/icons-material/Cached';
import _SaveIcon from '@mui/icons-material/Save';
import _RestoreIcon from '@mui/icons-material/Restore';
import EastIcon from '@mui/icons-material/East';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import _PaymentsIcon from '@mui/icons-material/Payments';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';
import { ReactComponent as CheckinIconSource } from './check-in.svg';
import { ReactComponent as CheckoutIconSource } from './check-out.svg';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ArrowLeft, ArrowRight, ArrowDropDown } from '@mui/icons-material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CollectionsIcon from '@mui/icons-material/Collections';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PeopleIcon from '@mui/icons-material/People';
import BoyIcon from '@mui/icons-material/Boy';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import WifiIcon from '@mui/icons-material/Wifi';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import KitchenIcon from '@mui/icons-material/Kitchen';
import FireplaceIcon from '@mui/icons-material/Fireplace';
import BalconyIcon from '@mui/icons-material/Balcony';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { Box } from '@mui/system';

//onClick, hidden, disabled, title, disabledTitle, loading

const iconStyle = ({ hidden = false }) => ({ theme }) => ({
    opacity: hidden ? 0 : 1,
    '&.MuiIconButton-sizeSmall': {
        padding: '0px',
        width: '24px',
        height: '24px',
    },
    '&.MuiIconButton-sizeMedium, &.MuiCircularProgress-medium': {
        padding: '0px',
        width: '40px',
        height: '40px',
    }
});

const errorStyle = ({ hidden = false }) => ({
    opacity: hidden ? 0 : 1,
    color: 'error.main',
    '&:hover': {
        color: 'error.main',
    }
});

const warningStyle = ({ hidden = false }) => ({
    opacity: hidden ? 0 : 1,
    color: 'warning.light',
    '&:hover': {
        color: 'warning.light',
    }
});

export const buttonize = (Component, defaultTitle) => ({ ...props }) => {
    const StyledIconButton = styled(IconButton)(iconStyle(props));

    const ExtendedIconButton = withLoading(withTooltip(StyledIconButton, defaultTitle));

    const { disabled = false, hidden = false } = props;
    const onClick = (disabled || hidden) ? undefined : props.onClick;

    const { childProps, ...rest } = props;

    return <ExtendedIconButton {...rest} onClick={onClick} disabled={disabled || hidden}><Component {...childProps} /></ExtendedIconButton>
}

export const withTooltip = (Component, defaultTitle = '') => ({ ...props }) => {
    const { title, disabledTitle = title, tooltipProps, ...rest } = props;
    const { hidden, disabled } = props;

    if (hidden) {
        return <Box sx={{ display: 'flex' }}><Component {...rest} /></Box>
    }
    else return <Tooltip {...tooltipProps} title={disabled ? disabledTitle : (title || defaultTitle)} disableHoverListener={disabled}>
        <Box sx={{ display: 'flex', width: 'min-content', height: 'min-content' }}>
            <Component {...rest} />
        </Box>
    </Tooltip>;
}

export const withLoading = (Component) => ({ ...props }) => {
    const { loading, ...rest } = props;
    const { size = 'medium' } = props;

    const style = {
        '&.CircularProgress-medium': {
            width: '24px !important',
            height: '24px !important',
            padding: '8px',
        },
        '&.CircularProgress-small': {
            width: '16px !important',
            height: '16px !important',
            padding: '4px',
        },
    }

    if (loading) {
        return <CircularProgress sx={style} className={`CircularProgress-${size}`} {...rest} />;
    }

    else return <Component {...rest} />;
}

export const EditIcon = buttonize(_EditIcon, "Edit");
export const DeleteIcon = buttonize(_DeleteIcon, "Delete");
export const SaveIcon = buttonize(_SaveIcon, "Save");
export const RefreshIcon = buttonize(_CachedIcon, "Refresh");
export const RestoreIcon = buttonize(_RestoreIcon, "Reset changes");
export const ExitIcon = buttonize(EastIcon, "Exit");
export const CalendarIcon = buttonize(CalendarMonth, "Calendar");
export const PlusIcon = buttonize(AddIcon, "Add");
export const MinusIcon = buttonize(RemoveIcon, "Remove");
export const RightArrow = buttonize(ArrowRight);
export const LeftArrow = buttonize(ArrowLeft);
export const DownArrow = buttonize(ArrowDropDown);
export const MoveUpIcon = buttonize(ArrowUpwardIcon, 'Move up');
export const MoveDownIcon = buttonize(ArrowDownwardIcon, 'Move down');
export const PaymentsIcon = buttonize(_PaymentsIcon);
export const RestrictionsButton = buttonize(EventBusyIcon);
export const PaymentPaidButton = buttonize(CheckCircleIcon, 'Mark as paid')
export const PicturesIcon = buttonize(CollectionsIcon, 'Pictures')

export const CheckinIcon = ({ style, ...props }) => <CheckinIconSource style={{ width: '1.5em', height: '1.5em', ...style }} {...props} />
export const CheckOutIcon = ({ style, ...props }) => <CheckoutIconSource style={{ width: '1.5em', height: '1.5em', ...style }} {...props} />

export const ErrorIcon = (props) => {
    const { sx, ...rest } = props;

    const Icon = withTooltip(ErrorOutlineIcon, "Error");
    return <Icon sx={{ ...errorStyle(props), ...sx }} {...rest} />
}

export const WarningIcon = (props) => {
    const { sx, ...rest } = props;

    const Icon = withTooltip(WarningAmberIcon, "Warning");
    return <Icon sx={{ ...warningStyle(props), ...sx }} {...rest} />
}

/* Added wrappers for uploader icons */
export const ZoomIcon = buttonize(ZoomInIcon, 'Preview image');
export const DragIcon = buttonize(DragIndicatorIcon, 'Drag to reorder');
export const UploadIcon = buttonize(UploadFileIcon, 'Upload images');

/* Facility icons for accommodations/rooms - exported as plain icons (not buttonized) */
export { PeopleIcon, BoyIcon, BedIcon, BathtubIcon, WifiIcon, AcUnitIcon, KitchenIcon, FireplaceIcon, BalconyIcon, SquareFootIcon, AspectRatioIcon };